import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type {
  SaveSourceSystemSettingsRequest,
  SourceSystemSettingsDTO,
  SourceType,
} from '../types/source-system';

/**
 * Fetches and saves settings for the source system right-pane settings panel.
 *
 * Provides:
 *   - settings: the current SourceSystemSettingsDTO (null while loading)
 *   - loading:  initial fetch in progress
 *   - saving:   save request in progress
 *   - error:    last fetch/save error message
 *   - save:     call to PUT /systems/:type/settings
 *   - reload:   re-fetch the settings
 */
export function useSourceSystemSettings(projectUuid: string, sourceType: SourceType) {
  const [settings, setSettings] = React.useState<SourceSystemSettingsDTO | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sourceSystemApi.getSettings(projectUuid, sourceType);
      setSettings(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectUuid, sourceType]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const save = React.useCallback(
    async (body: SaveSourceSystemSettingsRequest): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        const updated = await sourceSystemApi.saveSettings(projectUuid, sourceType, body);
        setSettings(updated);
        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [projectUuid, sourceType]
  );

  return { settings, loading, saving, error, save, reload };
}
